import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. Our team replies within 1 business day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary">Contact</p>
          <h1 className="mt-3 text-5xl sm:text-6xl tracking-[-0.04em] font-semibold text-slate-900 max-w-3xl">Talk to a human.</h1>
          <p className="mt-5 max-w-2xl text-lg text-slate-600">For partnerships, infrastructure questions, or to escalate an active campaign.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Detail Icon={Mail} l="Email" v="ops@connex.is" />
            <Detail Icon={Phone} l="Phone" v="+44 20 3728 6215" />
            <Detail Icon={MapPin} l="Headquarters" v={<>71-75 Shelton Street<br />Covent Garden, London<br />WC2H 9JQ<br /><span className="text-slate-500 text-[12.5px]">United Kingdom</span></>} />
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="text-xs uppercase tracking-widest text-slate-500">Status</div>
              <div className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-emerald-700"><span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" /> All systems operational</div>
              <div className="mt-3 text-xs font-mono text-slate-500">Last updated · 02:14 GMT</div>
            </div>
          </div>
          <form onSubmit={submit} className="lg:col-span-3 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></div>
              <div><Label>Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></div>
              <div className="sm:col-span-2"><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" /></div>
              <div className="sm:col-span-2"><Label>Message</Label><Textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" /></div>
            </div>
            <Button type="submit" disabled={busy} className="mt-6 rounded-full px-5 h-11" data-testid="contact-submit">{busy ? "Sending…" : "Send message"}</Button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Detail({ Icon, l, v }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-3">
      <span className="inline-grid place-items-center size-9 rounded-md bg-slate-900 text-white"><Icon className="size-4" strokeWidth={1.6} /></span>
      <div><div className="text-xs uppercase tracking-widest text-slate-500">{l}</div><div className="mt-1 font-medium text-slate-900">{v}</div></div>
    </div>
  );
}
